// apps/web/src/pages/author/EditorShell.tsx
//
// Phase 8 Plan 07 Task 3 — EditorShell scaffold (authr-01).
//
// PURPOSE: parent layout that wraps the editor toolbar + the SplitPane outer
// body + the UnsavedChangesGuard. Per UI-SPEC §"Author UI shell"
// component-tree (lines 109-130):
//
//   <RecipeEditor>
//     ├── <Form>                    (RHF FormProvider — owned by RecipeEditor)
//     │   └── <EditorShell>
//     │       ├── <EditorToolbar>   (toolbar prop)
//     │       ├── <SplitPane>       (children — left=RecipeFormPanel, right=PreviewPane)
//     │       └── <UnsavedChangesGuard />
//
// SCOPE — Plan 07 ships ONLY this layout scaffold. The caller (Plan 10
// RecipeEditor) owns:
//   - FormProvider instantiation + form context.
//   - Toolbar prop wiring (Save / Discard / Load-fixture / Paste-JSON handlers).
//   - SplitPane left=RecipeFormPanel + right=PreviewPane composition.
//
// SCOPE — Plan 10 will supply integration tests at the RecipeEditor level.
// Plan 07 ships no unit tests for EditorShell because the component is
// pure layout — a Plan 07 unit test would be a tautology.
//
// FORMSTATE DISCIPLINE: EditorShell does NOT read formState. The toolbar
// (a child slot) is the sole formState reader; the guard reads `isDirty` at
// EditorShell-peer level (toolbar peer — per discipline doc in
// EditorToolbar.tsx file header).

import { UnsavedChangesGuard } from "./UnsavedChangesGuard";

export interface EditorShellProps {
  /** Toolbar slot — the caller passes <EditorToolbar ... /> here. */
  toolbar: React.ReactNode;
  /** Body slot — the caller passes <SplitPane left=<RecipeFormPanel /> right=<PreviewPane /> />. */
  children: React.ReactNode;
}

export function EditorShell(props: EditorShellProps): React.ReactElement {
  const { toolbar, children } = props;
  return (
    <main aria-label="Recipe editor" className="flex flex-col h-full">
      {toolbar}
      <div className="flex-1 overflow-hidden">{children}</div>
      <UnsavedChangesGuard />
    </main>
  );
}
